import { useSiteStrips, useApiClient, type SiteStripAd } from '@weather-story/shared-hooks';
import { X, ExternalLink } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const DISMISS_KEY = 'sticky-ad-dismissed';

/**
 * Slim, dismissible sticky ad bar pinned to bottom of viewport.
 * Shows one random SiteStrip product. Only on mobile for anon/free users.
 * Dismissed for the session when the user taps X.
 */
const useAdDebug = () =>
  useState(
    () =>
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('addebug') === '1'
  )[0];

export function StickyBottomAd() {
  const isDebug = useAdDebug();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [scrolledPastHero, setScrolledPastHero] = useState(false);

  const apiClient = useApiClient();
  const [shuffleSeed] = useState(() => Math.random());
  const { data } = useSiteStrips({ limit: 5 });

  const product: SiteStripAd | null = useMemo(() => {
    if (!data?.data?.length) return null;
    void shuffleSeed;
    return shuffleArray(data.data)[0];
  }, [data?.data, shuffleSeed]);

  // Only show after user has scrolled 300px (past hero)
  useEffect(() => {
    const handler = () => setScrolledPastHero(window.scrollY > 300);
    window.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Ignore
    }
  };

  const handleClick = (id: string) => {
    apiClient.post(`/sitestrips/${id}/click`, {}).catch(() => {});
  };

  if (dismissed || !scrolledPastHero || !product) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-40 md:hidden animate-slide-up ${isDebug ? 'ring-2 ring-green-400 ring-offset-2 ring-offset-gray-900' : ''}`}
    >
      <div className="bg-gray-900/95 backdrop-blur-sm border-t border-purple-500/30 px-3 py-2.5 shadow-2xl relative">
        {isDebug && (
          <div className="absolute -top-3 left-3 bg-green-500 text-black text-[9px] font-mono font-bold px-2 py-0.5 rounded-full z-10">
            StickyAd · SiteStrip · id:{product.id.slice(0, 8)}
          </div>
        )}
        <div className="flex items-center gap-3">
          {/* Product image */}
          {(product.imageUrls?.[0] || product.imageUrl) && (
            <img
              src={product.imageUrls?.[0] ?? product.imageUrl ?? ''}
              alt=""
              className="w-10 h-10 rounded-lg object-contain bg-gray-800 flex-shrink-0"
              loading="lazy"
            />
          )}

          {/* Title + price */}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-200 truncate">{product.title}</p>
            <div className="flex items-center gap-2">
              {product.price && (
                <span className="text-xs font-bold text-purple-300">{product.price}</span>
              )}
              <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-500/80 bg-amber-500/10 px-1.5 py-0.5 rounded">
                Sponsored
              </span>
            </div>
          </div>

          {/* CTA button */}
          <a
            href={product.affiliateUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={() => handleClick(product.id)}
            className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-500 transition-colors flex-shrink-0"
          >
            {product.ctaText}
            <ExternalLink className="h-3 w-3" />
          </a>

          {/* Dismiss */}
          <button
            onClick={handleDismiss}
            className="p-1.5 text-gray-500 hover:text-gray-300 transition-colors flex-shrink-0"
            aria-label="Dismiss ad"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
